/**
 * Shared GLSL noise, injected into every procedural material.
 *
 * Kept in one place so the sun and the planets agree on what noise looks like —
 * two different hashes would make a lava world and a star surface feel like
 * they came from different games.
 */
export const NOISE_GLSL = /* glsl */ `
  float hash13(vec3 p) {
    p = fract(p * 0.3183099 + vec3(0.71, 0.113, 0.419));
    p *= 17.0;
    return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
  }

  float valueNoise(vec3 x) {
    vec3 i = floor(x);
    vec3 f = fract(x);
    f = f * f * (3.0 - 2.0 * f);

    return mix(
      mix(mix(hash13(i + vec3(0,0,0)), hash13(i + vec3(1,0,0)), f.x),
          mix(hash13(i + vec3(0,1,0)), hash13(i + vec3(1,1,0)), f.x), f.y),
      mix(mix(hash13(i + vec3(0,0,1)), hash13(i + vec3(1,0,1)), f.x),
          mix(hash13(i + vec3(0,1,1)), hash13(i + vec3(1,1,1)), f.x), f.y),
      f.z);
  }

  /** Octave count is a compile-time constant so the loop unrolls. */
  float fbm(vec3 p, int octaves) {
    float total = 0.0;
    float amplitude = 0.5;
    for (int i = 0; i < 8; i++) {
      if (i >= octaves) break;
      total += valueNoise(p) * amplitude;
      p *= 2.03;
      amplitude *= 0.5;
    }
    return total;
  }

  /**
   * Ridged noise — sharp creases instead of soft blobs.
   * Used for lava cracks, mountain chains and ice fractures.
   */
  float ridged(vec3 p, int octaves) {
    float total = 0.0;
    float amplitude = 0.5;
    for (int i = 0; i < 8; i++) {
      if (i >= octaves) break;
      total += (1.0 - abs(valueNoise(p) * 2.0 - 1.0)) * amplitude;
      p *= 2.11;
      amplitude *= 0.5;
    }
    return total;
  }

  /**
   * Warped noise: sample the field through an offset taken from the field
   * itself. This is what turns even blobs into swirls and continents, and it is
   * most of why the planets read as places rather than as static.
   */
  float warped(vec3 p, int octaves, float strength) {
    vec3 offset = vec3(
      fbm(p + vec3(1.7, 9.2, 3.3), 3),
      fbm(p + vec3(8.3, 2.8, 5.1), 3),
      fbm(p + vec3(4.1, 6.6, 7.9), 3)
    );
    return fbm(p + offset * strength, octaves);
  }
`;
